import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, RefreshCw } from "lucide-react";
import { api, copyText } from "@/lib/api";

const LOOK = {
  ok: { Icon: CheckCircle2, cls: "text-emerald-600", ring: "border-emerald-200 bg-emerald-50/60", label: "Siap" },
  warn: { Icon: AlertTriangle, cls: "text-amber-600", ring: "border-amber-200 bg-amber-50/60", label: "Perlu dicek" },
  fail: { Icon: XCircle, cls: "text-lm-red", ring: "border-red-200 bg-red-50/60", label: "Belum siap" },
};

export default function GoLive() {
  const [d, setD] = useState(null);
  const load = () => api.get("/go-live/checklist").then((r) => setD(r.data))
    .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat checklist"));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-ink-soft">Memuat checklist…</div>;

  return (
    <div className="space-y-6" data-testid="admin-go-live">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Checklist Go-Live</h1>
          <p className="mt-2 text-sm text-ink-soft max-w-2xl">
            Semua yang harus hijau sebelum LapakMahjong.id dipakai penuh untuk transaksi asli.
            Klik tombol di setiap baris untuk langsung memperbaikinya.
          </p>
        </div>
        <button data-testid="golive-refresh" onClick={load}
          className="h-10 px-4 rounded-md border border-black/15 text-sm flex items-center gap-2">
          <RefreshCw size={14} /> Cek ulang
        </button>
      </div>

      <div className="grid sm:grid-cols-4 gap-3" data-testid="golive-score">
        <div className="bg-ink text-cream rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-widest text-cream/60">Kesiapan</div>
          <div className="mt-1.5 text-3xl font-semibold">{d.score}%</div>
          <div className="mt-1 text-[11px] text-cream/60">
            {d.ready ? "Semua wajib sudah beres" : "Masih ada item wajib"}
          </div>
        </div>
        {["ok", "warn", "fail"].map((k) => {
          const { Icon, cls, label } = LOOK[k];
          return (
            <div key={k} className="bg-white border border-black/10 rounded-lg p-4" data-testid={`golive-count-${k}`}>
              <div className="text-[10px] uppercase tracking-widest text-ink-soft flex items-center gap-1.5">
                <Icon size={12} className={cls} /> {label}
              </div>
              <div className="mt-1.5 text-3xl font-semibold">{d.counts[k]}</div>
              <div className="mt-1 text-[11px] text-ink-soft">dari {d.total} item</div>
            </div>
          );
        })}
      </div>

      <div className="space-y-2.5">
        {d.items.map((it) => {
          const { Icon, cls, ring } = LOOK[it.status] || LOOK.warn;
          return (
            <div key={it.key} data-testid={`golive-item-${it.key}`}
              className={`rounded-lg border p-4 flex items-start gap-3 ${ring}`}>
              <Icon size={18} className={`${cls} shrink-0 mt-0.5`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">{it.label}</div>
                <div className="text-xs text-ink-soft mt-0.5 break-words">{it.detail}</div>
                {it.hint && <div className="mt-1.5 text-[11px] text-ink bg-white/70 border border-black/10 rounded px-2 py-1.5 break-all">{it.hint}</div>}
              </div>
              {it.action_path && (
                <Link to={it.action_path} data-testid={`golive-action-${it.key}`}
                  className="shrink-0 text-xs px-3 py-2 rounded-md border border-black/15 bg-white hover:border-ink/40">
                  {it.action_label || "Buka"}
                </Link>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-black/10 rounded-lg p-4" data-testid="golive-webhook">
        <div className="text-sm font-semibold">URL Webhook Midtrans</div>
        <div className="mt-1 text-xs text-ink-soft">
          Tempel URL ini di Midtrans Dashboard → Settings → Configuration → Payment Notification URL.
        </div>
        <div className="mt-2 flex gap-2 items-center flex-wrap">
          <code className="text-xs bg-cream-dark px-2 py-1.5 rounded break-all">{d.webhook_url}</code>
          <button data-testid="golive-copy-webhook"
            onClick={async () => (await copyText(d.webhook_url)
              ? toast.success("URL webhook dicopy")
              : toast.error("Gagal copy — silakan pilih teksnya lalu copy manual"))}
            className="text-xs px-3 py-1.5 rounded-md border border-black/15">Copy</button>
        </div>
      </div>
    </div>
  );
}
